import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { dmsService } from '../../services/dmsService';
import { userService } from '../../services/userService';

import { RootState } from '../../store';
import { UploadDocModal } from './components/UploadDocModal';
import { EditDocModal } from './components/EditDocModal';
import { ReplaceVersionModal } from './components/ReplaceVersionModal';
import { HistoryModal } from './components/HistoryModal';

const DmsApp: React.FC = () => {
  const [documents, setDocuments] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [documentTypes, setDocumentTypes] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Filter States
  const [filters, setFilters] = useState({
    q: '',
    category: '',
    department: '',
    document_type: '',
    uploader: '',
    file_type: '',
    created_after: '',
  });

  // Modal States
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isReplaceOpen, setIsReplaceOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any | null>(null);

  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const canDownload = currentUser?.role === 'super_admin' || currentUser?.role === 'admin';

  const [viewMode, setViewMode] = useState<'active' | 'archived'>('active');

  const fetchDocuments = async (activeFilters: any = {}, mode: 'active' | 'archived' = viewMode) => {
    setIsLoading(true);
    try {
      // Build clean parameters object, ignoring empty strings
      const params: Record<string, string> = {};
      Object.keys(activeFilters).forEach((key) => {
        if (activeFilters[key]) {
          params[key] = activeFilters[key];
        }
      });

      const data = mode === 'active'
        ? await dmsService.getDocuments(params)
        : await dmsService.getArchivedDocuments(params);
      setDocuments(data);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMetadataOptions = async () => {
    try {
      const [categoriesData, documentTypesData, departmentsData] = await Promise.all([
        dmsService.getCategories(),
        dmsService.getDocumentTypes(),
        userService.getDepartments(),
      ]);
      setCategories(categoriesData);
      setDocumentTypes(documentTypesData);
      setDepartments(departmentsData);
    } catch (err) {
      console.error('Failed to load categories/document types/departments:', err);
    }
  };

  useEffect(() => {
    fetchDocuments();
    fetchMetadataOptions();
  }, []);

  const handleFilterChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleApplySearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchDocuments(filters);
  };

  const handleClearSearch = () => {
    const cleared = {
      q: '',
      category: '',
      department: '',
      document_type: '',
      uploader: '',
      file_type: '',
      created_after: '',
    };
    setFilters(cleared);
    fetchDocuments(cleared);
  };

  // Logout is now handled in the Profile Page

  const handleDownload = async (doc: any) => {
    try {
      const res = await dmsService.downloadDocument(doc.id);
      if (res.download_url) {
        window.location.href = res.download_url;
      }
    } catch (e) {
      console.error('Download failed', e);
      alert('Failed to generate secure download link.');
    }
  };

  const handleArchive = async (id: string | number) => {
    if (window.confirm('Are you sure you want to archive this document?')) {
      try {
        await dmsService.archiveDocument(id);
        fetchDocuments(filters);
      } catch (e) {
        console.error('Archiving failed', e);
        alert('Failed to archive document.');
      }
    }
  };

  return (
    <div className="page-container">
      <div className="page-header" style={{ marginBottom: '1.5rem' }}>
        <div className="header-row">
          <h1 className="page-title" style={{ margin: 0 }}>DOME-Document Operations & Management Engine</h1>
          <div className="action-cell" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
            {currentUser?.role === 'super_admin' && (
              <button className="secondary-btn sm-btn" onClick={() => navigate('/admin/users')}>
                User Management
              </button>
            )}
            <button
              onClick={() => navigate('/profile')}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--primary)', display: 'flex', alignItems: 'center' }}
              title="Profile"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="32" height="32">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
        </div>
        <p className="page-description" style={{ margin: 0, marginTop: '0.5rem' }}>
          Secure repository for critical records with automated 12-month archiving.
        </p>
      </div>

      <div className="tab-nav" style={{ marginBottom: '1.5rem' }}>
        <button
          className={`tab-btn${viewMode === 'active' ? ' active' : ''}`}
          onClick={() => {
            setViewMode('active');
            fetchDocuments(filters, 'active');
          }}
        >
          Active Documents
        </button>
        <button
          className={`tab-btn${viewMode === 'archived' ? ' active' : ''}`}
          onClick={() => {
            setViewMode('archived');
            fetchDocuments(filters, 'archived');
          }}
        >
          Archived Documents
        </button>
      </div>

      <div className="actions-header">
        <button
          className="secondary-btn"
          onClick={() => setShowFilters(!showFilters)}
          style={{
            backgroundColor: showFilters ? 'rgba(71, 49, 146, 0.05)' : 'white',
          }}
        >
          <svg
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            width="18"
            height="18"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          {showFilters ? 'Hide Search' : 'Search / Filter'}
        </button>
        {viewMode === 'active' && (
          <button className="primary-btn" onClick={() => setIsUploadOpen(true)}>
            <svg
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              width="18"
              height="18"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
              />
            </svg>
            Upload Document
          </button>
        )}
      </div>


      {showFilters && (
        <form onSubmit={handleApplySearch} className="filter-panel">
          <div className="filter-group">
            <label>Document Name</label>
            <input
              type="text"
              name="q"
              value={filters.q}
              onChange={handleFilterChange}
              placeholder="e.g. Annual Report"
            />
          </div>
          <div className="filter-group">
            <label>Category</label>
            <select
              name="category"
              value={filters.category}
              onChange={handleFilterChange}
            >
              <option value="">All Categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.department_detail?.name ? `${c.name} (${c.department_detail.name})` : c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Department</label>
            <select
              name="department"
              value={filters.department}
              onChange={handleFilterChange}
            >
              <option value="">All Departments</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Document Type</label>
            <select
              name="document_type"
              value={filters.document_type}
              onChange={handleFilterChange}
            >
              <option value="">All Document Types</option>
              {documentTypes.map((dt) => (
                <option key={dt.id} value={dt.id}>
                  {dt.name}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Uploader Name</label>
            <input
              type="text"
              name="uploader"
              value={filters.uploader}
              onChange={handleFilterChange}
              placeholder="Search by user"
            />
          </div>
          <div className="filter-group">
            <label>File Type</label>
            <select
              name="file_type"
              value={filters.file_type}
              onChange={handleFilterChange}
            >
              <option value="">Any Format</option>
              <option value="pdf">PDF</option>
              <option value="docx">Word (DOCX)</option>
              <option value="xlsx">Excel (XLSX)</option>
              <option value="png">PNG Image</option>
              <option value="jpeg">JPEG Image</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Upload Date From</label>
            <input
              type="date"
              name="created_after"
              value={filters.created_after}
              onChange={handleFilterChange}
            />
          </div>
          <div className="filter-actions">
            <button
              type="button"
              className="secondary-btn"
              onClick={handleClearSearch}
            >
              Clear
            </button>
            <button type="submit" className="primary-btn">
              Apply Search
            </button>
          </div>
        </form>
      )}

      <div className="list-container">
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-light)' }}>
            Loading documents...
          </div>
        ) : documents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-light)' }}>
            {viewMode === 'active' ? 'No active documents found.' : 'No archived documents found.'}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Document Name</th>
                <th>Category</th>
                <th>Department</th>
                <th>Uploaded By</th>
                <th>Upload Date</th>
                <th>Document</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc, idx) => (
                <tr 
                  key={doc.id}
                  onClick={() => {
                    setSelectedDoc(doc);
                    setIsHistoryOpen(true);
                  }}
                  className="clickable-row"
                >
                  <td>
                    <strong>{idx + 1}</strong>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontWeight: 600 }}>{doc.title}</span>
                      {doc.document_type_detail && (
                        <div style={{ marginTop: '0.15rem' }}>
                          <span
                            style={{
                              fontSize: '0.75rem',
                              backgroundColor: 'rgba(71, 49, 146, 0.08)',
                              color: 'var(--primary)',
                              padding: '0.05rem 0.35rem',
                              borderRadius: '4px',
                              display: 'inline-block',
                              fontWeight: 500,
                            }}
                          >
                            {doc.document_type_detail.name}
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>{doc.category_detail?.name || 'N/A'}</td>
                  <td>{doc.category_detail?.department_detail?.name || '—'}</td>
                  <td>
                    {doc.uploaded_by_detail?.full_name ||
                      doc.uploaded_by_detail?.user_name ||
                      'System'}
                  </td>
                  <td>{new Date(doc.created_at).toLocaleDateString()}</td>
                  <td>
                    {doc.current_version_detail ? (
                      <div className="action-cell">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16" style={{ color: 'var(--primary)' }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>v{doc.current_version_detail.version_number}</span>
                      </div>
                    ) : (
                      <span className="text-muted">No file</span>
                    )}
                  </td>
                  <td>
                    <span
                      className="status-badge"
                      style={{
                        backgroundColor:
                          doc.confidentiality === 'PUBLIC'
                            ? '#d1fae5'
                             : doc.confidentiality === 'INTERNAL'
                            ? '#e0f2fe'
                            : doc.confidentiality === 'CONFIDENTIAL'
                            ? '#fef3c7'
                            : '#fee2e2',
                        color:
                          doc.confidentiality === 'PUBLIC'
                            ? '#065f46'
                            : doc.confidentiality === 'INTERNAL'
                            ? '#0369a1'
                            : doc.confidentiality === 'CONFIDENTIAL'
                            ? '#b45309'
                            : '#b91c1c',
                      }}
                    >
                      {doc.confidentiality}
                    </span>
                  </td>
                  <td>
                    <div className="action-cell">
                      {viewMode === 'archived' ? (
                        doc.is_archived ? (
                          <button
                            className="secondary-btn sm-btn"
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (window.confirm('Are you sure you want to restore this document to the active list?')) {
                                try {
                                  await dmsService.updateDocument(doc.id, { is_archived: false });
                                  alert('Document restored successfully.');
                                  fetchDocuments(filters);
                                } catch (err: any) {
                                  console.error('Failed to restore document:', err);
                                  alert(err.message || 'Failed to restore document.');
                                }
                              }
                            }}
                            style={{ borderColor: 'var(--primary)', color: 'var(--primary)' }}
                            title="Restore Document"
                          >
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="14" height="14">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 8H17" />
                            </svg>
                            Restore
                          </button>
                        ) : (
                          <span style={{ fontSize: '0.85rem', color: 'var(--text-light)', fontStyle: 'italic' }}>
                            Active (Has Archived Versions)
                          </span>
                        )
                      ) : (
                        <>
                          {canDownload && (
                            <button
                              className="secondary-btn sm-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(doc);
                              }}
                              title="Download"
                            >
                              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="14" height="14">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              Download
                            </button>
                          )}
                          <button
                            className="secondary-btn sm-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedDoc(doc);
                              setIsReplaceOpen(true);
                            }}
                          >
                            {doc.current_version_detail ? `Upload v${doc.current_version_detail.version_number + 1}` : 'Upload File'}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal Renderings */}
      <UploadDocModal
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        onSave={() => {
          setIsUploadOpen(false);
          fetchMetadataOptions();
          fetchDocuments(filters);
        }}
        onMetadataCreated={fetchMetadataOptions}
        categories={categories}
        documentTypes={documentTypes}
      />

      <EditDocModal
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setSelectedDoc(null);
        }}
        onSave={() => {
          setIsEditOpen(false);
          setSelectedDoc(null);
          fetchMetadataOptions();
          fetchDocuments(filters);
        }}
        onMetadataCreated={fetchMetadataOptions}
        categories={categories}
        documentTypes={documentTypes}
        document={selectedDoc}
      />

      <ReplaceVersionModal
        isOpen={isReplaceOpen}
        onClose={() => {
          setIsReplaceOpen(false);
          setSelectedDoc(null);
        }}
        onSave={() => {
          setIsReplaceOpen(false);
          setSelectedDoc(null);
          fetchDocuments(filters);
        }}
        document={selectedDoc}
      />

      <HistoryModal
        isOpen={isHistoryOpen}
        onClose={() => {
          setIsHistoryOpen(false);
          setSelectedDoc(null);
        }}
        document={selectedDoc}
        onEditClick={() => {
          setIsHistoryOpen(false);
          setIsEditOpen(true);
        }}
        onRestore={() => {
          setIsHistoryOpen(false);
          setSelectedDoc(null);
          fetchDocuments(filters);
        }}
        onRefresh={() => {
          fetchDocuments(filters);
        }}
        isArchivedView={viewMode === 'archived'}
      />

    </div>
  );
};

export default DmsApp;
